/**
 * AccessStateScreens.tsx — MER Algo Footprints
 *
 * Phase-2 P2-D13 IMPL-4 (D-P2AT-26). The screens the commercial gate shows
 * when it does not let a signed-in user into the commercial application.
 *
 * These REPLACE the old access-pending screen. It said "Subscription required"
 * and invited users to "request access or activate your subscription" —
 * workflows MER does not have. Every sentence here describes something true
 * today.
 *
 * Three distinct states, deliberately never merged:
 *   · checking     — the server has not answered yet
 *   · not enabled  — the server answered: no commercial access
 *   · unverified   — the server could not answer
 *
 * None of them signs the user out or sends them to /login: an access state is
 * not an identity problem, and every screen keeps account security and
 * sign-out reachable.
 */

import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

// ── Shared frame ──────────────────────────────────────────────────────────────

function AccessFrame({ status, title, children }: {
  status: string; title: string; children?: React.ReactNode;
}) {
  return (
    <div className="mer-full-page" style={{
      background: '#080F1C',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Courier New', Courier, monospace",
    }}>
      <div style={{ maxWidth: 420, width: '100%', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 10, letterSpacing: 4, color: '#C9A84C', fontWeight: 700, marginBottom: 32 }}>
          MER ALGO FOOTPRINTS
        </div>
        <div style={{
          background: 'rgba(13,31,53,0.7)', border: '1px solid #1A3050',
          borderRadius: 4, padding: '32px 28px',
        }}>
          <div style={{
            display: 'inline-block', padding: '5px 12px', marginBottom: 24,
            background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: 20, fontSize: 10, letterSpacing: 1.5, color: '#C9A84C',
          }}>
            {status}
          </div>
          <h2 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, letterSpacing: 1, color: '#E8EDF5' }}>
            {title}
          </h2>
          {children}
        </div>
      </div>
    </div>
  );
}

const TEXT: React.CSSProperties = { margin: '0 0 20px', fontSize: 12, color: '#5A7FA8', lineHeight: 1.7 };
const BUTTON: React.CSSProperties = {
  background: 'transparent', border: '1px solid #1A3050', borderRadius: 3,
  color: '#8BACC8', fontSize: 11, letterSpacing: 1, padding: '8px 14px',
  cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit',
};

/** Account & security, and sign-out — reachable from every access state. */
function AccountActions() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const signOut = useCallback(async () => {
    await logout();
    navigate('/', { replace: true });
  }, [logout, navigate]);
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
      <Link to="/app/security" style={BUTTON}>Account &amp; security</Link>
      <button type="button" style={BUTTON} onClick={signOut}>Sign out</button>
    </div>
  );
}

// ── Checking ──────────────────────────────────────────────────────────────────

export function AccessCheckingScreen() {
  return (
    <AccessFrame status="CHECKING" title="Checking access…">
      <p style={TEXT}>One moment while MER confirms access for this account.</p>
    </AccessFrame>
  );
}

// ── Not enabled (commerciallyEntitled === false) ──────────────────────────────

export function AccessNotEnabledScreen() {
  return (
    <AccessFrame status="ACCESS" title="Access to MER">
      <p style={TEXT}>
        Access to MER is not currently enabled for this account.
        Please contact a MER administrator.
      </p>
      <AccountActions />
    </AccessFrame>
  );
}

// ── Unverified (commerciallyEntitled === null) ────────────────────────────────

export function AccessUnverifiedScreen({ onRetry, retrying }: {
  onRetry: () => void; retrying: boolean;
}) {
  return (
    <AccessFrame status="UNVERIFIED" title="Unable to verify access">
      <p style={TEXT}>
        MER couldn't verify your access right now.
        Please try again shortly.
      </p>
      <div style={{ marginBottom: 16 }}>
        <button type="button" style={BUTTON} onClick={onRetry} disabled={retrying}>
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
      </div>
      <AccountActions />
    </AccessFrame>
  );
}
